/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import CardLayout from "../Cards/CardLayout";
import { EChartsOption } from "echarts";
import { useUtils } from "../../customHooks/useUtils";
import { useFetchData } from "../../customHooks/useGetDashboardData";
import Loader from "../feedBacks/loader";
import CardError from "../feedBacks/CardError";

type ApiResponse = {
  total: number;
  dataset: { key: string; value: number }[];
};

const CustomerBreakDownChart = ({ company_id }: { company_id: string }) => {
  const { t, FEEDBACK_MESSAGES } = useUtils();

  // Generate YYYY-MM-01 from month number (1 = Jan, 12 = Dec)
  const getMonthDate = (monthNumber: number) => {
    const date = new Date();
    const year = date.getFullYear();

    // If monthNumber > currentMonthNumber, it means it's from the previous year
    if (monthNumber > date.getMonth() + 1) {
      return `${year - 1}-${String(monthNumber).padStart(2, "0")}-01`;
    }
    return `${year}-${String(monthNumber).padStart(2, "0")}-01`;
  };

  const currentMonthNumber = new Date().getMonth() + 1;

  const filterOptions = useMemo(() => {
    const monthsList = [
      { key: t("month.jan"), num: 1 },
      { key: t("month.feb"), num: 2 },
      { key: t("month.mar"), num: 3 },
      { key: t("month.apr"), num: 4 },
      { key: t("month.may"), num: 5 },
      { key: t("month.jun"), num: 6 },
      { key: t("month.jul"), num: 7 },
      { key: t("month.aug"), num: 8 },
      { key: t("month.sep"), num: 9 },
      { key: t("month.oct"), num: 10 },
      { key: t("month.nov"), num: 11 },
      { key: t("month.dec"), num: 12 },
    ];

    // Only include months from Jan up to the current month
    return monthsList
      .filter((m) => m.num <= currentMonthNumber)
      .map((m) => ({
        key: m.key,
        value: getMonthDate(m.num),
      }));
  }, [t, currentMonthNumber]);

  const currentMonthValue = getMonthDate(currentMonthNumber);

  const [startDate, setStartDate] = React.useState(() => {
    return (
      filterOptions.find((item) => item.value === currentMonthValue)?.value ||
      currentMonthValue
    );
  });

  const { data, isLoading, isError } = useFetchData<ApiResponse>(
    ["customerBreakdown", company_id, startDate],
    "finance/monthly-device-payment-status",
    {
      company_id,
      start_date: startDate,
      end_date: startDate,
    }
  );

  if (isLoading) {
    return (
      <CardLayout
        title={t("chart.customer_breakdown")}
        className="min-w-[300px] lg:max-w-[450px] flex-1"
      >
        <div className="flex justify-center items-center h-full w-full">
          <Loader />
        </div>
      </CardLayout>
    );
  }

  if (isError || !data) {
    return (
      <CardLayout
        title={t("chart.customer_breakdown")}
        className="min-w-[300px] lg:max-w-[450px] flex-1"
      >
        <CardError message={FEEDBACK_MESSAGES.ERROR} className="!h-[100px]" />
      </CardLayout>
    );
  }

  // const totalValue = data.dataset.reduce((sum, item) => sum + item.value, 0);

  const options: EChartsOption = {
    tooltip: {
      trigger: "item",
      formatter: (params: any) => {
        return `${params.name}: ${params.value}`;
      },
    },
    legend: {
      top: "80%",
      left: "center",
      data: data.dataset.map(
        (item) =>
          `${
            item.key === "paying"
              ? t("chart.label_paying")
              : t("chart.label_defaulting")
          } (${item.value})`
      ),
    },
    series: [
      {
        type: "pie",
        data: data.dataset.map((item) => ({
          ...item,
          name: `${
            item.key === "paying"
              ? t("chart.label_paying")
              : t("chart.label_defaulting")
          } (${item.value})`,
          itemStyle: {
            borderRadius: 10,
            borderColor: "#fff",
            borderWidth: 6,
            color: (item.key === "paying"
              ? t("chart.label_paying")
              : t("chart.label_defaulting")
            ).includes(t("chart.label_paying"))
              ? "#FFC000"
              : "#7C878E",
          },
        })),
        radius: ["40%", "70%"],
        center: ["50%", "35%"],
        avoidLabelOverlap: false,
        label: { show: false, position: "center" },
        emphasis: { label: { show: false } },
        labelLine: { show: false },
      },
    ],
  };

  return (
    <CardLayout
      title={t("chart.customer_breakdown")}
      className="min-w-[300px] lg:max-w-[450px] flex-1"
      besideTitleComponent={
        <select
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="min-w-[120px]  cursor-pointer flex items-center justify-center p-2 rounded-full bg-[#E5E5E5] text-primary-blackMain text-[10px]"
        >
          {filterOptions.map((item) => (
            <option
              key={item.key}
              value={item.value}
              className="text-primary-blackLight cursor-pointer"
            >
              {item.key}
            </option>
          ))}
        </select>
      }
      besideComponentStyle="!flex-1 max-w-[70%] !justify-between"
    >
      <div className="flex flex-col h-full w-full relative">
        <div className="absolute inset-0 top-[-15%] h-full flex flex-col items-center justify-center">
          <span className="text-3xl font-semibold">{data.total}</span>
          <span className="text-md font-semibold">
            {t("site_details.location")}
          </span>
        </div>
        <ReactECharts option={options} />
      </div>
    </CardLayout>
  );
};

export default CustomerBreakDownChart;
